'use client';

import { useState, type InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

interface FloatingInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
}

export const FloatingInput = forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ label, value, onChange, onSubmit, type = 'text', className, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = value.length > 0;
    const isFloating = isFocused || hasValue;

    return (
      <div className="relative">
        <label
          className={cn(
            'absolute left-4 transition-all duration-200 pointer-events-none font-medium',
            isFloating
              ? 'top-0 -translate-y-1/2 text-[11px] px-2 bg-bg-0 text-text-2'
              : 'top-1/2 -translate-y-1/2 text-[15px] text-text-3'
          )}
        >
          {label}
        </label>

        <input
          ref={ref}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onSubmit) {
              e.preventDefault();
              onSubmit();
            }
          }}
          className={cn(
            'w-full h-11 px-4 text-[15px] font-medium',
            'bg-surface-2 text-text-1 border border-border-subtle rounded-xl',
            'transition-all duration-200 focus:outline-none',
            isFocused
              ? 'border-focus-1 ring-0'
              : 'hover:border-text-3/20',
            className
          )}
          {...props}
        />
      </div>
    );
  }
);

FloatingInput.displayName = 'FloatingInput';

export type { FloatingInputProps };
